/**
 * This file defines the invite() service.
 */

import OriginTracer from '@atlassiansox/origin-tracing';

import { fg } from '@atlaskit/platform-feature-flags';
import { AnyAri } from '@atlassian/ari/any-ari';

import type { AnyServiceWithDeps, DependenciesContext } from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getErrorDetails,
	getStargateURL,
	isAcceptableFailure,
	isAcceptableFailureForUserInvite,
	type Product,
	shouldReportFailedInvite,
} from '../common/common';
import { fetchJSON } from '../common/fetchJSON';

import { getContinueUrlWithOrigin } from './utils';

type User = {
	email?: string;
	id?: string;
};

type InviteSuccessResult = User & {
	integration?: string;
	origin?: OriginTracer;
	resource: AnyAri;
	touchpoint: EnablementTouchpoint;
} & (
		| {
				error: null;
				inviteStatus: 'INVITED';
		  }
		| {
				error: null;
				inviteStatus: 'USER_EXISTS';
		  }
		| {
				error: null;
				inviteStatus: 'INVITED_PENDING_APPROVAL';
		  }
		| {
				error: null;
				inviteStatus: 'PENDING_INVITE_EXISTS';
		  }
		| {
				error: null;
				inviteStatus: 'NOT_INVITED';
		  }
		| {
				error: InviteErrorReason;
				inviteStatus: 'ERROR';
		  }
	);

type InviteFailedResult = {
	error: Error;
	errorReason: InviteErrorReason;
	// When entire request fails, for example, bad request, network error, etc.
	// TODO: This can be more specific, but we need to know what the response status is.
	inviteStatus: null;
};

export type InviteResult = InviteSuccessResult | InviteFailedResult;

export const INVITE_STATUSES = [
	'INVITED',
	'USER_EXISTS',
	'INVITED_PENDING_APPROVAL',
	'PENDING_INVITE_EXISTS',
	'NOT_INVITED',
	'ERROR',
] as const;

export type InviteStatus = (typeof INVITE_STATUSES)[number];

type InviteErrorReason = {
	code: string;
	message: string;
	status?: number;
};

export type InviteResponse = (User & {
	errorReasons?: {
		[resource: string]: InviteErrorReason;
	};
	results: {
		[resource: string]: InviteStatus;
	};
})[];

export interface RecaptchaOptions {
	recaptchaAction?: string;
	recaptchaToken?: string;
}

type GetAdditionalAttributes = (user: User) => Record<string, unknown>;

// Input - data directly needed to invite a user/s to a product:
export type InviteInput = {
	/**
	 * The URL the invitee lands on when accepting the invite.
	 *
	 * If a continueUrl is not provided, the invitee will be redirected to the product landing page.
	 *
	 * NOTE: The continueUrl should not contain the Origin Tracing query parameter: atlOrigin=
	 * as this will be overridden by the service.
	 * @default undefined
	 */
	continueUrl?: string;
	getAdditionalAttributes?: GetAdditionalAttributes;
	integration?: string;
	recaptchaOptions?: RecaptchaOptions;
	resources: AnyAri[];
	touchpoint: EnablementTouchpoint;
	users: User[];
};

// Dependencies - generally common to all invites:
const dependencies = {
	fetchJSON,
	createAnalyticsEvent,
	getStargateURL,
	getCurrentUserId,
	getCurrentCloudId,
	getCurrentProduct,
};

type Deps = typeof dependencies;

export const invite = Object.assign(inviteImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function inviteImpl(input: InviteInput, context: DependenciesContext<Deps>) {
	const deps = context.dependencies();
	let results: InviteResult[];

	const usersWithOrigin = input.users.map((user) => {
		const origin = new OriginTracer({ product: deps.getCurrentProduct() });
		const continueUrl = getContinueUrlWithOrigin(
			input.continueUrl,
			input.resources,
			origin,
			input.touchpoint.replace(/ /g, '_'),
		);
		return {
			user: {
				...user,
				continueUrl,
			},
			origin,
		};
	});

	const users = usersWithOrigin.map(({ user }) => user);

	try {
		const response = await deps.fetchJSON<InviteResponse>({
			url: deps.getStargateURL('/v3/invitations/invite'),
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(input.recaptchaOptions?.recaptchaToken
					? { 'G-Recaptcha-Token': input.recaptchaOptions.recaptchaToken }
					: {}),
				...(input.recaptchaOptions?.recaptchaAction
					? { 'Recaptcha-Action': input.recaptchaOptions.recaptchaAction }
					: {}),
			},
			body: JSON.stringify({
				users: fg('expansion-services-in-core-invites') ? users : input.users,
				resources: input.resources.map((resource) => resource.toString()),
				...(fg('expansion-services-in-core-invites')
					? {}
					: {
							continueUrl: input.continueUrl,
						}),
				...(fg('expansion-services-in-core-invites')
					? {
							metadata: {
								touchpoint: input.touchpoint,
								sourceProduct: deps.getCurrentProduct(),
							},
						}
					: {}),
			}),
		});
		results = mapInviteResponse(response, usersWithOrigin, input.touchpoint, input.integration);
	} catch (error) {
		const errorReason = getErrorDetails(error);
		const { code, message, status } = errorReason;
		const attributes: ErrorAnalyticsAttributes = {
			code: code,
			message: message,
			status: status,
		};

		if (!isAcceptableFailure(message, status)) {
			inviteFailedSLO(input.touchpoint, attributes, context);
		}
		results = [{ inviteStatus: null, error: toError(error), errorReason }];
	}

	results.forEach((result) => fireInviteAnalytics(result, context, input.getAdditionalAttributes));
	return results;
}

export const mapInviteResponse = (
	response: InviteResponse,
	usersWithOrigin: { origin: OriginTracer; user: User }[],
	touchpoint: EnablementTouchpoint,
	integration?: string,
): InviteResult[] =>
	response.flatMap((user) => {
		const origin = usersWithOrigin.find(
			(userWithOrigin) =>
				userWithOrigin.user.id === user.id || userWithOrigin.user.email === user.email,
		)?.origin;
		return Object.keys(user.results).map((resource) => {
			const inviteStatus = user.results[resource];
			const resourceAri = AnyAri.parse(resource);
			const common = {
				id: user.id,
				email: user.email,
				origin,
				integration,
				touchpoint,
				resource: resourceAri,
			};

			if (inviteStatus === 'ERROR') {
				return {
					...common,
					inviteStatus,
					// errorReasons should always be present for error responses
					error: user.errorReasons?.[resource] ?? {
						code: 'UNKNOWN_ERROR',
						message: 'Unknown error',
					},
				};
			}

			return {
				...common,
				inviteStatus,
				error: null,
			};
		});
	});

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

interface BulkInviteResult {
	error: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'NOT_INVITED' | 'ERROR' }>,
		'email'
	>[];
	failure: Extract<InviteResult, { inviteStatus: null }> | undefined;
	invited: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED' | 'USER_EXISTS' }>,
		'email' | 'id'
	>[];
	requested: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED_PENDING_APPROVAL' | 'PENDING_INVITE_EXISTS' }>,
		'email' | 'id'
	>[];
}

const isInvited = (res: InviteResult): res is BulkInviteResult['invited'][number] => {
	return res.inviteStatus === 'INVITED' || res.inviteStatus === 'USER_EXISTS';
};

const isRequested = (res: InviteResult): res is BulkInviteResult['requested'][number] => {
	return (
		res.inviteStatus === 'INVITED_PENDING_APPROVAL' || res.inviteStatus === 'PENDING_INVITE_EXISTS'
	);
};

const isError = (res: InviteResult): res is BulkInviteResult['error'][number] => {
	return res.inviteStatus === 'NOT_INVITED' || res.inviteStatus === 'ERROR';
};

export const mapInviteResult = (result: InviteResult[]) =>
	result.reduce<BulkInviteResult>(
		(accumulator, res) => {
			switch (res.inviteStatus) {
				case 'INVITED':
				case 'USER_EXISTS':
					isInvited(res) && accumulator.invited.push(res);
					break;
				case 'INVITED_PENDING_APPROVAL':
				case 'PENDING_INVITE_EXISTS':
					isRequested(res) && accumulator.requested.push(res);
					break;
				case 'NOT_INVITED':
				case 'ERROR':
					isError(res) && accumulator.error.push(res);
					break;
				default:
					if (fg('expansion-services-in-core-invites')) {
						accumulator.failure = res;
					} else {
						throw new Error('Unexpected return type from invite user response');
					}
			}
			return accumulator;
		},
		{
			invited: [],
			requested: [],
			error: [],
			failure: undefined,
		},
	);

// Analytics functions for these common events might later become
// exported for standalone use
function fireInviteAnalytics(
	result: InviteResult,
	context: DependenciesContext<Deps>,
	getAdditionalAttributes?: GetAdditionalAttributes,
) {
	if (!result.error) {
		const originAttributes =
			result.origin?.toAnalyticsAttributes({
				hasGeneratedId: true,
			}) ?? {};
		const attributes: AnalyticsAttributes = {
			...(getAdditionalAttributes?.(result) ?? {}),
			...originAttributes,
			// The id is always present for success responses
			userAaid: result.id!,
			actorAaid: context.dependencies().getCurrentUserId(),
			inviteStatus: result.inviteStatus,
			targetProduct: result.resource.resourceOwner as Product,
			targetTenantId: result.resource.resourceId,
			sourceProduct: context.dependencies().getCurrentProduct(),
			sourceTenantId: context.dependencies().getCurrentCloudId(),
			integration: result.integration,
			touchpoint: result.touchpoint,
		};

		inviteSucceededSLO(result.touchpoint, context);

		if (result.inviteStatus === 'INVITED') {
			fireUserInvited(attributes, context);
		}
		if (result.inviteStatus === 'INVITED_PENDING_APPROVAL') {
			fireProductAccessRequested(attributes, context);
		}
	} else if (result.inviteStatus === 'ERROR') {
		const failure = result.error;
		// There should not be a failure SLO event if the error is a user network failure error
		if (!isAcceptableFailureForUserInvite(failure)) {
			if (shouldReportFailedInvite(failure)) {
				inviteFailedSLO(result.touchpoint, failure, context);
			} else {
				inviteSucceededSLO(result.touchpoint, context);
			}
		}
	}
}

type OriginAttributes = ReturnType<OriginTracer['toAnalyticsAttributes']>;
/**
 * Valid values are defined in IIS:
 * https://bitbucket.org/atlassian/id-invitations-service/src/master/src/main/java/com/atlassian/identity/invitations/model/EnablementTouchpoint.java
 */
const EnablementTouchpoint = {
	CORE_INVITES: 'Core Invites',
	SHARE_DIALOG: 'Share Dialog',
	TEAMS_MENU: 'Teams Menu',
	MENTIONS: 'Mentions',
	JIRA_ASSIGNS: 'Jira Assigns',
	PROMPT_INVITE_ON_LINK_INSERT: 'Prompt invite on link insert',
	CROSSJOIN_NUDGE: 'Crossjoin Nudge',
} as const;

type EnablementTouchpoint = (typeof EnablementTouchpoint)[keyof typeof EnablementTouchpoint];

export type TrackEventAttributes = {
	actorAaid: string; // AAID of user initiating request (same as above if RA1/3, different if RA2)
	integration?: string; // Integration being used to invite the user
	inviteStatus: InviteStatus; // USER_EXISTS, INVITED_PENDING_APPROVAL
	recommendationSources?: string[]; // Source of user invited (eg Google, Slack, Microsoft, Collab Graph?)
	sourceProduct: Product; // Product being initiated from (in case of server event with different )
	sourceTenantId: string; // TenantID of the product where the request was initiated
	targetProduct: Product; // Product being requested to
	targetTenantId: string; // TenantID of the product where the request is for
	touchpoint: EnablementTouchpoint; // Required attribute as source can't always be set in Jira
	userAaid: string; // AAID of user who the request is for
	userUuid?: string; // For 3P, if we don't have AAID yet: UUID of user who the request is for
};

type AnalyticsAttributes = TrackEventAttributes & Partial<OriginAttributes>;

type ErrorAnalyticsAttributes = {
	code: string; // Error code from request response
	message: string; // Error message from request response
	status?: number; // Numerical error status code from request response
};

function fireProductAccessRequested(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'productAccess',
				action: 'requested',
				actionSubjectId: attributes.targetProduct,
				source: attributes.touchpoint,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
}

function fireUserInvited(attributes: AnalyticsAttributes, context: DependenciesContext<Deps>) {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'user',
				action: 'invited',
				actionSubjectId: attributes.userAaid,
				source: attributes.touchpoint,
				attributes,
				tags: ['growth'],
			},
		})
		.fire();
}

const inviteFailedSLO = (
	touchpoint: EnablementTouchpoint,
	attributes: ErrorAnalyticsAttributes,
	context: DependenciesContext<Deps>,
) => {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'sloInviteRequest',
				action: 'failed',
				source: touchpoint,
				attributes: { ...attributes, touchpoint },
				tags: ['growth'],
			},
		})
		.fire();
};

const inviteSucceededSLO = (
	touchpoint: EnablementTouchpoint,
	context: DependenciesContext<Deps>,
) => {
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'sloInviteRequest',
				action: 'succeeded',
				source: touchpoint,
				attributes: { touchpoint },
				tags: ['growth'],
			},
		})
		.fire();
};
